import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import StateSelect from "./StateSelect";

const emptyForm = { name: "", address: "", gstin: "", phone: "", stateCode: "" };

const Distributors = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadDistributors = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/distributors");
      setDistributors(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load distributors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDistributors();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (distributor) => {
    setEditingId(distributor.distributorId);
    setFormData({
      name: distributor.name || "",
      address: distributor.address || "",
      gstin: distributor.gstin || "",
      phone: distributor.phone || "",
      stateCode: distributor.stateCode || "",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Every GSTIN's first 2 digits are that state's official GST code — pre-fill them so the user
  // only has to type the rest, replacing just the leading digits if some GSTIN was already typed.
  const handleStateSelect = (state) => {
    setFormData((prev) => ({ ...prev, gstin: state.gstCode + prev.gstin.slice(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/distributors/${editingId}`, formData);
        toast.success("Distributor updated");
      } else {
        await api.post("/api/v1/distributors", formData);
        toast.success("Distributor created");
      }
      setShowModal(false);
      loadDistributors();
    } catch (error) {
      toast.error(error.message || "Failed to save distributor");
    } finally {
      setSaving(false);
    }
  };

  const filteredDistributors = distributors.filter((d) =>
    [d.name, d.gstin, d.distributorId].some((value) => String(value || "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading distributors...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        A distributor is a separate company using this system — its own customers, products, invoices and users,
        kept apart from every other distributor. Create one here first, then assign users to it from Admin → Users.
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name / GSTIN / ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="primary" onClick={openCreate}>
          + New Distributor
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Address</th>
              <th>GSTIN</th>
              <th>Phone</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDistributors.length === 0 ? (
              <tr>
                <td colSpan={7}>No distributors found.</td>
              </tr>
            ) : (
              filteredDistributors.map((d) => (
                <tr key={d.distributorId}>
                  <td>{d.distributorId}</td>
                  <td>{d.name}</td>
                  <td>{d.address || "-"}</td>
                  <td>{d.gstin || "-"}</td>
                  <td>{d.phone || "-"}</td>
                  <td>{d.stateCode || "-"}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(d)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Distributor" : "Create Distributor"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input name="address" value={formData.address} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>GSTIN</label>
                  <input name="gstin" value={formData.gstin} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" value={formData.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>State Code</label>
                  <StateSelect value={formData.stateCode} onChange={handleChange} onStateSelect={handleStateSelect} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Distributors;
